/**
 * Who is signed in. The access token lives ONLY in memory (never localStorage);
 * a page reload restores the session through the HttpOnly refresh cookie.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Me, SessionResponse } from '@shared/api/types';
import type { ConfigurablePage } from '@shared/constants/pages';

export type SessionStatus = 'checking' | 'authenticated' | 'anonymous';
/** 'user' = they pressed Sign out (no ?next= on the login page afterwards). */
export type SignOutReason = 'user' | 'expired';

export interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  user: Me | null;
  pages: Partial<Record<ConfigurablePage, boolean>>;
  signOutReason: SignOutReason | null;
}

const initialState: SessionState = {
  status: 'checking',
  accessToken: null,
  user: null,
  pages: {},
  signOutReason: null,
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    sessionReceived(state, action: PayloadAction<SessionResponse>) {
      state.status = 'authenticated';
      state.accessToken = action.payload.accessToken;
      state.user = action.payload.user;
      state.pages = action.payload.pages;
      state.signOutReason = null;
    },
    profileReceived(state, action: PayloadAction<{ user: Me; pages: SessionResponse['pages'] }>) {
      state.user = action.payload.user;
      state.pages = action.payload.pages;
    },
    signedOut(_state, action: PayloadAction<SignOutReason | undefined>) {
      return { ...initialState, status: 'anonymous' as const, signOutReason: action.payload ?? 'expired' };
    },
  },
  selectors: {
    selectSessionStatus: (s) => s.status,
    selectAccessToken: (s) => s.accessToken,
    selectCurrentUser: (s) => s.user,
    selectPages: (s) => s.pages,
    selectIsAdmin: (s) => s.user?.isAdmin ?? false,
    selectSignOutReason: (s) => s.signOutReason,
  },
});

export const { sessionReceived, profileReceived, signedOut } = sessionSlice.actions;
export const {
  selectSessionStatus,
  selectAccessToken,
  selectCurrentUser,
  selectPages,
  selectIsAdmin,
  selectSignOutReason,
} = sessionSlice.selectors;
